import { useState, useEffect } from 'react';
import { 
  HomepageProductSection, 
  HeroSectionData, 
  PromotionalBannerData,
  ProductData 
} from '../types';
import { storefrontService } from '../services/storefrontService';

interface StorefrontData {
  homepageSections: HomepageProductSection[];
  heroSection: HeroSectionData | null;
  promotionalBanner: PromotionalBannerData | null;
  loading: boolean;
  error: string | null;
}

export const useStorefrontData = () => {
  const [data, setData] = useState<StorefrontData>({
    homepageSections: [],
    heroSection: null,
    promotionalBanner: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    loadStorefrontData();
  }, []);

  const loadStorefrontData = async () => {
    try {
      setData(prev => ({ ...prev, loading: true, error: null }));
      
      const [sections, hero, banner] = await Promise.all([
        storefrontService.getHomepageProductSections(),
        storefrontService.getHeroSectionData(),
        storefrontService.getPromotionalBannerData()
      ]);

      setData({
        homepageSections: sections,
        heroSection: hero,
        promotionalBanner: banner,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error loading storefront data:', error);
      setData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load storefront data'
      }));
    }
  };

  // Convert HomepageProductSection to ProductData for compatibility
  const getProductData = (): ProductData[] => {
    return data.homepageSections
      .filter(section => section.isActive)
      .sort((a, b) => a.order - b.order)
      .map(section => ({
        title: section.title,
        description: section.description,
        buttonText: section.buttonText,
        buttonLink: section.buttonLink,
        imageSrc: section.imageSrc,
        imageAlt: section.imageAlt,
        reverse: section.reverse,
        backgroundColor: section.backgroundColor
      }));
  };

  const getHeroImageSrc = (): string => {
    return data.heroSection?.imageSrc || '/images/homepage-products/IMG_1.webp';
  };

  const getPromotionalBannerData = () => {
    if (!data.promotionalBanner || !data.promotionalBanner.isActive) {
      return null;
    }
    
    return {
      message: data.promotionalBanner.message,
      code: data.promotionalBanner.code,
      dates: data.promotionalBanner.dates,
      note: data.promotionalBanner.note
    };
  };

  return {
    ...data,
    getProductData,
    getHeroImageSrc,
    getPromotionalBannerData,
    refetch: loadStorefrontData
  };
};
